import { PublicUrlAdapter, FileUrlVariant } from '@magicdoorinc/file';
import { Skeleton, Image, cn, createTriggerResource, createFileUrl } from '@magicdoorinc/ui';
import { Show, splitProps, createMemo, createEffect } from 'solid-js';
import { Dynamic } from 'solid-js/web';
import { DeactivatedFlag } from '~/components/common';
import DisplayAddress from '~/components/common/DisplayAddress';
import { pickProps, TitleWithFeedbackIcon } from '~/components/common/FeedbackIcon/TitleWithFeedbackIcon';
import { PropertiesRepository } from '~/repositories/propertiesRepository';
import { emptyPlaceholder, propertyTypesDict } from '~/utils/constant';
import { getPublicFileURL } from '~/utils/prefix';
import type { Component, JSX } from 'solid-js';
import type { TitleWithFeedbackIconProps } from '~/components/common/FeedbackIcon/TitleWithFeedbackIcon';
import type { HydratedPropertyDto } from '~/swagger/maintenance/Api';
import type { PropertyDto } from '~/swagger/portal/Api';
import type { PolymorphicComponent } from '~/utils/types';

const propertiesRepo = new PropertiesRepository();

export const PropertyImage: Component<{
  class?: string;
  property?: Pick<PropertyDto, 'id' | 'imageId'>;
}> = (props) => {
  const imageUrl = createFileUrl(
    () => (props.property?.imageId ? new PublicUrlAdapter({ reference: props.property.imageId }) : undefined),
    () => FileUrlVariant.Thumb
  );

  return (
    <Show when={props.property} fallback={<Skeleton class={cn('size-12', props.class)} data-slot="image" />}>
      <Image
        class={cn('bg-primary/20 object-cover', props.class)}
        src={imageUrl()?.url ?? getPublicFileURL('/img/property-thumb.webp')}
        fallback={getPublicFileURL('/img/property-thumb.webp')}
      />
    </Show>
  );
};

export const PropertyTitle: PolymorphicComponent<
  'div',
  {
    class?: string;
    property?: PropertyDto | HydratedPropertyDto;
    propertyId?: string;
    showType?: boolean;
    responsiveType?: boolean;
    typeClass?: string;
    addressClass?: string;
    warning?: JSX.Element;
  } & Partial<TitleWithFeedbackIconProps>
> = (props) => {
  const [, rest] = splitProps(props, ['class', 'property', 'propertyId']);

  const [loadedProperty, loadProperty] = createTriggerResource((id: string | undefined) => {
    if (!id) return undefined;
    return propertiesRepo.getProperty(id);
  });

  createEffect(() => {
    if (props.propertyId && !props.property) {
      loadProperty(props.propertyId);
    }
  });

  const effectiveProperty = createMemo(() => {
    if (props.property) return props.property;
    if (props.propertyId) {
      return loadedProperty();
    }
    return undefined;
  });

  return (
    <TitleWithFeedbackIcon
      {...pickProps(rest)}
      type="properties"
      entityId={effectiveProperty()?.id}
      initalSelectedIcons={effectiveProperty()?.icons}
      class="w-full items-start">
      <Dynamic component={props.as || 'div'} class={cn('flex max-w-full items-center gap-2 text-base text-text-02', props.class)} {...rest}>
        <Show when={effectiveProperty()} fallback={<Skeleton class="size-12 rounded-md" data-slot="image" />}>
          <PropertyImage class="size-[42px] min-w-[42px] shrink-0 rounded-md" property={effectiveProperty()} data-slot="image" />
        </Show>
        <Show
          when={effectiveProperty()}
          fallback={
            <>
              <Skeleton class="h-7 w-28" />
              <Skeleton class="h-4 w-44" />
            </>
          }>
          <div class="flex flex-1">
            <div class="min-w-0 flex-1 space-y-0.5 break-words text-left text-sm font-medium" data-slot="content">
              <div class={cn('flex flex-wrap items-center gap-1', props.responsiveType && 'flex-row items-start')}>
                <div class="flex items-center gap-2">
                  <h4 class="break-words text-sm" data-slot="title">
                    {effectiveProperty()?.name || emptyPlaceholder}
                    <DeactivatedFlag class="ml-2" active={effectiveProperty()?.active} />
                  </h4>
                  <Show when={props.warning}>
                    <div class="shrink-0 text-xs font-normal text-warning">{props.warning}</div>
                  </Show>
                </div>
                <Show when={props.showType}>
                  <div class={cn('inline-block', !props.responsiveType && 'ml-1.5')}>
                    <div
                      class={cn(
                        'flex items-center gap-1 rounded-3xl bg-black/20 px-2 py-0.5 text-xs font-normal capitalize',
                        props.typeClass
                      )}>
                      <Dynamic
                        component={propertyTypesDict.getMetaByValue(effectiveProperty()!.type)?.icon}
                        class="inline-block size-4 text-white"
                      />
                      <span class="hidden leading-[18px] lg:inline-block">
                        {propertyTypesDict.getLabelByValue(effectiveProperty()!.type)}
                      </span>
                    </div>
                  </div>
                </Show>
              </div>

              <DisplayAddress
                class={cn('text-xs font-normal text-text-03', props.addressClass)}
                address={effectiveProperty()?.address}
                data-slot="address"
              />
            </div>
          </div>
        </Show>
      </Dynamic>
    </TitleWithFeedbackIcon>
  );
};
